import { createReducer, createAction, createSelector } from 'redux-starter-kit';
import axios from 'axios';

import api from 'api/api';

export interface BusinessSectorType {
  id: number;
  code: string;
  level: number;
  fr_sector: string;
  en_sector: string;
  it_sector: string;
  de_sector: string;
  rev4: string;
  parent: number;
  label: string;
  value: string;
}

// State
interface AssistantState {
  loaded: boolean;
  categories: object[];
  category: {};
  subcategories: object[];
  audience: {
    segments: object[];
    size: {
      range: {
        min: number;
        max: number;
      };
    };
  };
  research: {
    goals: [];
  };
  countries: [];
  genders: [];
  customer: {
    types: [];
  };
  intlPrefix: string;
  sectors: {
    count: number | null;
    next: string | null;
    previous: string | null;
    results: BusinessSectorType[];
  };
  subsectors: {
    count: number | null;
    next: string | null;
    previous: string | null;
    results: BusinessSectorType[];
  };
}

export const initialState: AssistantState = {
  loaded: false,
  categories: [],
  category: {},
  subcategories: [],
  audience: {
    segments: [],
    size: {
      range: {
        min: 30,
        max: 20000,
      },
    },
  },
  research: {
    goals: [],
  },
  countries: [],
  genders: [],
  customer: {
    types: [],
  },
  intlPrefix: 'en_',
  sectors: {
    count: null,
    next: null,
    previous: null,
    results: [],
  },
  subsectors: {
    count: null,
    next: null,
    previous: null,
    results: [],
  },
};

// Actions
const ASSISTANT_LOADED = 'assistant/ASSISTANT_LOADED';
const GET_ASSISTANT_GENDERS = 'assistant/GET_ASSISTANT_GENDERS';
const GET_ASSISTANT_CATEGORIES = 'assistant/GET_ASSISTANT_CATEGORIES';
const GET_ASSISTANT_CATEGORY = 'assistant/GET_ASSISTANT_CATEGORY';
const SET_ASSISTANT_CATEGORY_SUBCATEGORIES =
  'assistant/SET_ASSISTANT_CATEGORY_SUBCATEGORIES';
const CLEAR_ASSISTANT_CATEGORY_SUBCATEGORIES =
  'assistant/CLEAR_ASSISTANT_CATEGORY_SUBCATEGORIES';
const GET_ASSISTANT_AUDIENCE_SEGMENTS =
  'assistant/GET_ASSISTANT_AUDIENCE_SEGMENTS';
const GET_ASSISTANT_COUNTRIES = 'assistant/GET_ASSISTANT_COUNTRIES';
const GET_ASSISTANT_TYPES = 'assistant/GET_ASSISTANT_TYPES';
const GET_ASSISTANT_RESEARCH_GOALS = 'assistant/GET_ASSISTANT_DATA';
const GET_ASSISTANT_AUDIENCE_SIZE = 'assistant/GET_ASSISTANT_AUDIENCE_SIZE';
const SET_ASSISTANT_BUSINESS_SUBSECTORS =
  'assistant/SET_ASSISTANT_BUSINESS_SUBSECTORS';
const SET_ASSISTANT_BUSINESS_SECTORS =
  'assistant/SET_ASSISTANT_BUSINESS_SECTORS';

const setAssistantLoaded = createAction(ASSISTANT_LOADED);
const getAssistantGenders = createAction(GET_ASSISTANT_GENDERS);
const getAssistantCategories = createAction(GET_ASSISTANT_CATEGORIES);
const getAssistantCategory = createAction(GET_ASSISTANT_CATEGORY);
const setAssistantCategorySubcategories = createAction(
  SET_ASSISTANT_CATEGORY_SUBCATEGORIES
);
const clearAssistantCategorySubcategories = createAction(
  CLEAR_ASSISTANT_CATEGORY_SUBCATEGORIES
);
const getAssistantAudienceSegments = createAction(
  GET_ASSISTANT_AUDIENCE_SEGMENTS
);
const getAssistantCountries = createAction(GET_ASSISTANT_COUNTRIES);
const getAssistantTypes = createAction(GET_ASSISTANT_TYPES);
const getAssistantResearchGoals = createAction(GET_ASSISTANT_RESEARCH_GOALS);
const getAssistantAudienceSize = createAction(GET_ASSISTANT_AUDIENCE_SIZE);
const setAssistantBusinessSubsectors = createAction(
  SET_ASSISTANT_BUSINESS_SUBSECTORS
);
const setAssistantBusinessSectors = createAction(
  SET_ASSISTANT_BUSINESS_SECTORS
);

export const assistantActions = {
  clearAssistantCategorySubcategories,
  setAssistantCategorySubcategories,
  getAssistantCategory,
  getAssistantData: () => (dispatch: any) => {
    axios
      .all([
        api.resources.getAssistantAudienceSegments(),
        api.resources.getAssistantGenders(),
        api.resources.getAssistantCountries(),
        api.resources.getAssistantTypes(),
        api.resources.getAssistantResearchGoals(),
        api.resources.getAssistantAudienceSize(),
        api.resources.getAssistantCategories(),
      ])
      .then(
        axios.spread(
          (
            audienceSegments,
            genders,
            countries,
            customerTypes,
            researchGoals,
            audienceSize,
            categories
          ) => {
            dispatch(setAssistantLoaded(true));
            dispatch(getAssistantAudienceSegments(audienceSegments.data));
            dispatch(getAssistantGenders(genders.data));
            dispatch(getAssistantCountries(countries.data.countries));
            dispatch(getAssistantTypes(customerTypes.data));
            dispatch(getAssistantResearchGoals(researchGoals.data));
            dispatch(getAssistantAudienceSize(audienceSize.data));
            dispatch(getAssistantCategories(categories.data));
          }
        )
      );
  },
  setAssistantBusinessSubsectors,
  setAssistantBusinessSectors,
};

// Selectors
export const assistantSelectors = {
  assistant: createSelector(['assistant']),
  assistantIsLoaded: createSelector(['assistant.loaded']),
  subcategories: createSelector(['assistant.subcategories']),
  segments: createSelector(['assistant.audience.segments']),
  categories: createSelector(['assistant.categories']),
  goals: createSelector(['assistant.research.goals']),
  genders: createSelector(['assistant.genders']),
  countries: createSelector(['assistant.countries']),
  sizeRange: createSelector(['assistant.audience.size.range']),
};

// Reducers
export default createReducer(initialState, {
  [GET_ASSISTANT_AUDIENCE_SEGMENTS]: (state, action) => {
    state.audience.segments = action.payload;
  },
  [GET_ASSISTANT_GENDERS]: (state, action) => {
    state.genders = action.payload;
  },
  [GET_ASSISTANT_COUNTRIES]: (state, action) => {
    state.countries = action.payload;
  },
  [GET_ASSISTANT_TYPES]: (state, action) => {
    state.customer.types = action.payload;
  },
  [GET_ASSISTANT_RESEARCH_GOALS]: (state, action) => {
    state.research.goals = action.payload;
  },
  [GET_ASSISTANT_RESEARCH_GOALS]: (state, action) => {
    state.research.goals = action.payload;
  },
  [GET_ASSISTANT_AUDIENCE_SIZE]: (state, action) => {
    state.audience.size = action.payload;
  },
  [GET_ASSISTANT_CATEGORIES]: (state, action) => {
    state.categories = action.payload;
  },
  [ASSISTANT_LOADED]: (state, action) => {
    state.loaded = action.payload;
  },
  [SET_ASSISTANT_CATEGORY_SUBCATEGORIES]: (state, action) => {
    state.subcategories = action.payload;
  },
  [CLEAR_ASSISTANT_CATEGORY_SUBCATEGORIES]: (state) => {
    state.subcategories = [];
  },
  [SET_ASSISTANT_BUSINESS_SUBSECTORS]: (state, action) => {
    state.subsectors = action.payload;
  },
  [SET_ASSISTANT_BUSINESS_SECTORS]: (state, action) => {
    state.sectors = action.payload;
  },
});
